<script>
    import { onMount } from 'svelte';
    import { currentUser } from './store.js';
    import { organizationsAPI } from './api.js';

    let organizations = [];
    let loading = false;
    let error = null;

    // Modal state
    let showOrgModal = false;
    let showUserModal = false;
    let editingOrg = null;
    let selectedOrg = null;

    // Form data
    let orgFormData = {
        name: '',
        domain: '',
        plan: 'free',
        max_agents: 2,
        max_channels: 1,
        is_active: true
    };

    let userFormData = {
        name: '',
        email: '',
        password: '',
        role: 'agent',
        is_active: true
    };

    const plans = [
        { value: 'free', label: 'Free', agents: 2, channels: 1 },
        { value: 'starter', label: 'Starter', agents: 5, channels: 3 },
        { value: 'pro', label: 'Professional', agents: 15, channels: 10 },
        { value: 'enterprise', label: 'Enterprise', agents: 999, channels: 999 }
    ];

    onMount(() => {
        loadOrganizations();
    });

    async function loadOrganizations() {
        loading = true;
        error = null;
        try {
            organizations = await organizationsAPI.getAll();
        } catch (err) {
            error = err.message || 'Error al cargar organizaciones';
            console.error('Error loading organizations:', err);
        } finally {
            loading = false;
        }
    }

    function openCreateOrgModal() {
        editingOrg = null;
        orgFormData = {
            name: '',
            domain: '',
            plan: 'free',
            max_agents: 2,
            max_channels: 1,
            is_active: true
        };
        showOrgModal = true;
    }

    function openEditOrgModal(org) {
        editingOrg = org;
        orgFormData = {
            name: org.name,
            domain: org.domain || '',
            plan: org.plan,
            max_agents: org.max_agents,
            max_channels: org.max_channels,
            is_active: org.is_active
        };
        showOrgModal = true;
    }

    function closeOrgModal() {
        showOrgModal = false;
        editingOrg = null;
    }

    async function saveOrganization() {
        if (!orgFormData.name) {
            alert('El nombre de la organización es requerido');
            return;
        }

        loading = true;
        error = null;

        try {
            if (editingOrg) {
                await organizationsAPI.update(editingOrg.id, orgFormData);
            } else {
                await organizationsAPI.create(orgFormData);
            }

            closeOrgModal();
            await loadOrganizations();
        } catch (err) {
            error = err.message || 'Error al guardar la organización';
            console.error('Error saving organization:', err);
        } finally {
            loading = false;
        }
    }

    async function deleteOrganization(orgId) {
        if (!confirm('¿Está seguro de desactivar esta organización? Todos los usuarios también serán desactivados.')) return;

        loading = true;
        error = null;

        try {
            await organizationsAPI.delete(orgId);
            await loadOrganizations();
        } catch (err) {
            error = err.message || 'Error al eliminar la organización';
            console.error('Error deleting organization:', err);
        } finally {
            loading = false;
        }
    }

    function openAddUserModal(org) {
        selectedOrg = org;
        userFormData = {
            name: '',
            email: '',
            password: '',
            role: 'agent',
            is_active: true
        };
        showUserModal = true;
    }

    function closeUserModal() {
        showUserModal = false;
        selectedOrg = null;
    }

    async function createUser() {
        if (!userFormData.name || !userFormData.email || !userFormData.password) {
            alert('Nombre, email y contraseña son requeridos');
            return;
        }

        loading = true;
        error = null;

        try {
            await organizationsAPI.createUser(selectedOrg.id, userFormData);
            closeUserModal();
            await loadOrganizations();
            alert('Usuario creado exitosamente');
        } catch (err) {
            error = err.message || 'Error al crear usuario';
            console.error('Error creating user:', err);
            alert('Error al crear usuario: ' + err.message);
        } finally {
            loading = false;
        }
    }

    function getPlanColor(plan) {
        const colors = {
            'free': '#757575',
            'starter': '#2196f3',
            'pro': '#9c27b0',
            'enterprise': '#ff9800'
        };
        return colors[plan] || '#757575';
    }

    function onPlanChange(e) {
        const selectedPlan = plans.find(p => p.value === e.target.value);
        if (selectedPlan) {
            orgFormData.plan = selectedPlan.value;
            orgFormData.max_agents = selectedPlan.agents;
            orgFormData.max_channels = selectedPlan.channels;
        }
    }
</script>

<style>
    .organizations-container {
        padding: 20px;
        max-width: 1400px;
        margin: 0 auto;
    }

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }

    .header h1 {
        margin: 0;
        color: #333;
    }

    .btn {
        padding: 10px 20px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.2s;
    }

    .btn-primary {
        background: #2196f3;
        color: white;
    }

    .btn-primary:hover {
        background: #1976d2;
    }

    .btn-secondary {
        background: #757575;
        color: white;
    }

    .btn-secondary:hover {
        background: #616161;
    }

    .btn-success {
        background: #4caf50;
        color: white;
    }

    .btn-success:hover {
        background: #388e3c;
    }

    .btn-danger {
        background: #f44336;
        color: white;
        padding: 6px 12px;
        font-size: 12px;
    }

    .btn-danger:hover {
        background: #d32f2f;
    }

    .btn-edit {
        background: #ff9800;
        color: white;
        padding: 6px 12px;
        font-size: 12px;
        margin-right: 8px;
    }

    .btn-edit:hover {
        background: #f57c00;
    }

    .btn-users {
        background: #9c27b0;
        color: white;
        padding: 6px 12px;
        font-size: 12px;
        margin-right: 8px;
    }

    .btn-users:hover {
        background: #7b1fa2;
    }

    .error-message {
        background: #ffebee;
        color: #c62828;
        padding: 12px 16px;
        border-radius: 4px;
        margin-bottom: 20px;
        border-left: 4px solid #c62828;
    }

    .loading {
        text-align: center;
        padding: 40px;
        color: #757575;
    }

    .orgs-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
        gap: 20px;
        margin-top: 20px;
    }

    .org-card {
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 20px;
        transition: all 0.2s;
        position: relative;
    }

    .org-card:hover {
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        transform: translateY(-2px);
    }

    .org-card.inactive {
        opacity: 0.6;
        background: #f5f5f5;
    }

    .org-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 15px;
    }

    .org-title {
        font-size: 20px;
        font-weight: 600;
        color: #333;
        margin: 0 0 5px 0;
    }

    .org-domain {
        font-size: 13px;
        color: #757575;
    }

    .plan-badge {
        display: inline-block;
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 11px;
        color: white;
        font-weight: 600;
        text-transform: uppercase;
    }

    .status-indicator {
        position: absolute;
        top: 10px;
        right: 10px;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #4caf50;
    }

    .status-indicator.inactive {
        background: #f44336;
    }

    .org-stats {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
        margin-bottom: 15px;
    }

    .stat-item {
        background: #f5f5f5;
        padding: 10px;
        border-radius: 4px;
    }

    .stat-label {
        font-size: 11px;
        color: #757575;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 4px;
    }

    .stat-value {
        font-size: 18px;
        font-weight: 700;
        color: #333;
    }

    .org-limits {
        display: flex;
        gap: 15px;
        margin-bottom: 15px;
        font-size: 13px;
        color: #666;
    }

    .org-actions {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
    }

    /* Modal */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .modal {
        background: white;
        border-radius: 8px;
        padding: 30px;
        width: 90%;
        max-width: 600px;
        max-height: 90vh;
        overflow-y: auto;
    }

    .modal h2 {
        margin: 0 0 20px 0;
        color: #333;
    }

    .form-group {
        margin-bottom: 20px;
    }

    .form-group label {
        display: block;
        margin-bottom: 8px;
        color: #555;
        font-weight: 500;
        font-size: 14px;
    }

    .form-group input,
    .form-group select {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        font-family: inherit;
    }

    .form-group small {
        display: block;
        margin-top: 5px;
        color: #757575;
        font-size: 12px;
    }

    .checkbox-group {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .checkbox-group input[type="checkbox"] {
        width: auto;
        cursor: pointer;
    }

    .form-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 15px;
    }

    .modal-actions {
        display: flex;
        gap: 10px;
        justify-content: flex-end;
        margin-top: 25px;
    }

    .empty-state {
        text-align: center;
        padding: 60px 20px;
        color: #757575;
    }

    .empty-state h3 {
        color: #333;
        margin-bottom: 10px;
    }
</style>

<div class="organizations-container">
    <div class="header">
        <h1>🏢 Administración de Organizaciones</h1>
        <button class="btn btn-primary" on:click={openCreateOrgModal}>
            + Nueva Organización
        </button>
    </div>

    {#if error}
        <div class="error-message">{error}</div>
    {/if}

    {#if loading && organizations.length === 0}
        <div class="loading">Cargando organizaciones...</div>
    {:else if organizations.length === 0}
        <div class="empty-state">
            <h3>No hay organizaciones</h3>
            <p>Crea la primera organización para empezar.</p>
        </div>
    {:else}
        <div class="orgs-grid">
            {#each organizations as org}
                <div class="org-card" class:inactive={!org.is_active}>
                    <div class="status-indicator" class:inactive={!org.is_active}></div>

                    <div class="org-header">
                        <div>
                            <h3 class="org-title">{org.name}</h3>
                            {#if org.domain}
                                <div class="org-domain">🌐 {org.domain}</div>
                            {/if}
                        </div>
                        <span class="plan-badge" style="background-color: {getPlanColor(org.plan)}">
                            {org.plan}
                        </span>
                    </div>

                    <div class="org-stats">
                        <div class="stat-item">
                            <div class="stat-label">Usuarios</div>
                            <div class="stat-value">{org.stats?.total_users || 0}</div>
                        </div>
                        <div class="stat-item">
                            <div class="stat-label">Activos</div>
                            <div class="stat-value">{org.stats?.active_users || 0}</div>
                        </div>
                        <div class="stat-item">
                            <div class="stat-label">Admins</div>
                            <div class="stat-value">{org.stats?.admins || 0}</div>
                        </div>
                        <div class="stat-item">
                            <div class="stat-label">Agentes</div>
                            <div class="stat-value">{org.stats?.agents || 0}</div>
                        </div>
                    </div>

                    <div class="org-limits">
                        <span>👥 Max Agentes: {org.max_agents}</span>
                        <span>📱 Max Canales: {org.max_channels}</span>
                    </div>

                    <div class="org-actions">
                        <button class="btn btn-users" on:click={() => openAddUserModal(org)}>
                            👤 Agregar Usuario
                        </button>
                        <button class="btn btn-edit" on:click={() => openEditOrgModal(org)}>
                            ✏️ Editar
                        </button>
                        <button class="btn btn-danger" on:click={() => deleteOrganization(org.id)}>
                            🗑️ Desactivar
                        </button>
                    </div>
                </div>
            {/each}
        </div>
    {/if}
</div>

<!-- Organization Modal -->
{#if showOrgModal}
    <div class="modal-overlay" on:click={closeOrgModal}>
        <div class="modal" on:click|stopPropagation>
            <h2>{editingOrg ? 'Editar Organización' : 'Nueva Organización'}</h2>

            <form on:submit|preventDefault={saveOrganization}>
                <div class="form-group">
                    <label for="name">Nombre de la Empresa/Organización *</label>
                    <input
                        id="name"
                        type="text"
                        bind:value={orgFormData.name}
                        placeholder="Ej: Acme Inc."
                        required
                    />
                </div>

                <div class="form-group">
                    <label for="domain">Dominio (Opcional)</label>
                    <input
                        id="domain"
                        type="text"
                        bind:value={orgFormData.domain}
                        placeholder="Ej: acme.com"
                    />
                    <small>Dominio personalizado de la organización.</small>
                </div>

                <div class="form-group">
                    <label for="plan">Plan</label>
                    <select id="plan" bind:value={orgFormData.plan} on:change={onPlanChange}>
                        {#each plans as plan}
                            <option value={plan.value}>
                                {plan.label} - {plan.agents} agentes, {plan.channels} canales
                            </option>
                        {/each}
                    </select>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label for="max_agents">Máx. Agentes</label>
                        <input
                            id="max_agents"
                            type="number"
                            bind:value={orgFormData.max_agents}
                            min="1"
                        />
                    </div>

                    <div class="form-group">
                        <label for="max_channels">Máx. Canales</label>
                        <input
                            id="max_channels"
                            type="number"
                            bind:value={orgFormData.max_channels}
                            min="1"
                        />
                    </div>
                </div>

                <div class="form-group">
                    <div class="checkbox-group">
                        <input
                            id="is_active"
                            type="checkbox"
                            bind:checked={orgFormData.is_active}
                        />
                        <label for="is_active">Organización activa</label>
                    </div>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" on:click={closeOrgModal}>
                        Cancelar
                    </button>
                    <button type="submit" class="btn btn-success" disabled={loading}>
                        {loading ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </form>
        </div>
    </div>
{/if}

<!-- User Modal -->
{#if showUserModal && selectedOrg}
    <div class="modal-overlay" on:click={closeUserModal}>
        <div class="modal" on:click|stopPropagation>
            <h2>Agregar Usuario a {selectedOrg.name}</h2>

            <form on:submit|preventDefault={createUser}>
                <div class="form-group">
                    <label for="user_name">Nombre Completo *</label>
                    <input
                        id="user_name"
                        type="text"
                        bind:value={userFormData.name}
                        placeholder="Ej: Juan Pérez"
                        required
                    />
                </div>

                <div class="form-group">
                    <label for="user_email">Email *</label>
                    <input
                        id="user_email"
                        type="email"
                        bind:value={userFormData.email}
                        placeholder="Ej: juan@empresa.com"
                        required
                    />
                </div>

                <div class="form-group">
                    <label for="user_password">Contraseña *</label>
                    <input
                        id="user_password"
                        type="password"
                        bind:value={userFormData.password}
                        placeholder="Mínimo 6 caracteres"
                        required
                    />
                </div>

                <div class="form-group">
                    <label for="user_role">Rol</label>
                    <select id="user_role" bind:value={userFormData.role}>
                        <option value="admin">Admin - Control total de la organización</option>
                        <option value="agent">Agente - Atender conversaciones</option>
                        <option value="viewer">Viewer - Solo lectura</option>
                    </select>
                </div>

                <div class="form-group">
                    <div class="checkbox-group">
                        <input
                            id="user_is_active"
                            type="checkbox"
                            bind:checked={userFormData.is_active}
                        />
                        <label for="user_is_active">Usuario activo</label>
                    </div>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" on:click={closeUserModal}>
                        Cancelar
                    </button>
                    <button type="submit" class="btn btn-success" disabled={loading}>
                        {loading ? 'Creando...' : 'Crear Usuario'}
                    </button>
                </div>
            </form>
        </div>
    </div>
{/if}
